import { Flex, Icon, Icons, Input, Span } from "@ir/ui";
import { useEffect, useState, type ReactNode } from "react";

const MIN_SEARCH_LENGTH = 3;
const MAX_SEARCH_LENGTH = 50;

/** Defaults match `minLength` / `maxLength` props. */
export const SEARCH_MIN_LENGTH = MIN_SEARCH_LENGTH;
export const SEARCH_MAX_LENGTH = MAX_SEARCH_LENGTH;

export interface SearchInputProps {
  placeholder?: string;
  onSearch?: (query: string) => void;
  searchQuery?: string;
  onClear: () => void;
  maxLength?: number;
  minLength?: number;
  /** Replaces the default search icon when provided (e.g. Master Data Search by). */
  prefix?: ReactNode;
  /** Optional trailing adornment (e.g. Master Data search icon on the right). */
  suffix?: ReactNode;
}

function searchErrorFeedback(
  searchError: "min" | "max" | null,
  minLength: number,
  maxLength: number,
): ReactNode {
  if (searchError === "min") {
    return (
      <Span className="text-grey-500 text-xs">
        Search must be at least {minLength} characters long.
      </Span>
    );
  }
  if (searchError === "max") {
    return (
      <Span className="text-grey-500 text-xs">
        Search must be at most {maxLength} characters long.
      </Span>
    );
  }
  return null;
}

const defaultSearchPrefix = (
  <Icon
    name={Icons.Search}
    size="small"
    color="grey"
    style={{ color: "var(--grey-400)" }}
  />
);

const SearchInput = ({
  placeholder,
  onSearch,
  searchQuery,
  onClear,
  maxLength = MAX_SEARCH_LENGTH,
  minLength = MIN_SEARCH_LENGTH,
  prefix,
  suffix,
}: SearchInputProps) => {
  const [draft, setDraft] = useState(searchQuery ?? "");
  const [searchError, setSearchError] = useState<"min" | "max" | null>(null);

  // Keep the displayed value in sync with the "applied" query from parent.
  useEffect(() => {
    setDraft(searchQuery ?? "");
  }, [searchQuery]);

  return (
    <Flex direction="column" gap={0.25} align="start">
      <Input
        type="text"
        aria-label="Search"
        style={{ minWidth: "300px" }}
        allowClear={true}
        placeholder={placeholder || "Search"}
        size="small"
        prefix={prefix ?? defaultSearchPrefix}
        suffix={suffix}
        value={draft}
        onChange={(e) => {
          const nextValue = e.target.value;
          setDraft(nextValue);
          const trimmed = nextValue.trim();
          if (trimmed.length === 0) {
            setSearchError(null);
            onClear?.();
            return;
          }
          if (trimmed.length > maxLength) {
            setSearchError("max");
            return;
          }
          if (trimmed.length >= minLength) {
            setSearchError(null);
            return;
          }
          // Too short for search: clear max if user shortened an over-long query; keep min from Enter.
          setSearchError((prev) => (prev === "max" ? null : prev));
        }}
        onClear={() => {
          setSearchError(null);
          onClear();
          setDraft("");
        }}
        minLength={minLength}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === "Enter") {
            const value = draft.trim();
            if (value.length === 0) {
              setSearchError(null);
            } else if (value.length < minLength) {
              setSearchError("min");
            } else if (value.length > maxLength) {
              setSearchError("max");
            } else {
              setSearchError(null);
              onSearch?.(value);
            }
            e.currentTarget.blur();
          }
        }}
      />
      {searchErrorFeedback(searchError, minLength, maxLength)}
    </Flex>
  );
};
export default SearchInput;
